<script setup lang="ts">
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import { useTokenType } from '~/composables/useTokenType'
import { TransactionActionModel } from '~/types/model/transaction.model'
import BcLink from '~/ui/Link/BcLink.vue'

interface IProps {
  row: TransactionActionModel
}

const props = defineProps<IProps>()

const { isErc20, isErc1155 } = useTokenType(props.row.tokenType)
</script>

<template>
  <div class="transfer-row-cont">
    <span class="text-lg text-primary/40">{{ row.event }}</span>
    <div class="flex w-full items-center gap-x-2 text-lg">
      <img
        class="img"
        :src="row.tokenInfo.image"
        alt="token image"
      >
      <BcLink
        :content="row.tokenInfo.name"
        :to="isErc20 ? routes.getTokensDetailPageWithTab(row.tokenInfo.address) :
          routes.getNftDetailPageWithTab(row.tokenType, row.tokenInfo.address)"
        class="truncate"
      />
      <template v-if="isErc20">
        <span class="truncate text-primary">{{ row.size }}</span><span
          class="max-w-[60px] shrink-0 truncate text-primary/40"
        >{{ row.tokenInfo.symbol }}</span>
      </template>
      <BcLink
        v-if="row.tokenId"
        :content="row.tokenName || `#${row.tokenId}`"
        :to="routes.getNftItemDetailPageWithTab(row.tokenType, row.tokenInfo.address, row.tokenId)"
        class="flex-auto basis-1/4 truncate"
      />
      <span
        v-if="isErc1155"
        class="ml-auto truncate text-primary"
      >{{ row.size }}</span>
    </div>
    <div class="transfer-container">
      <div class="flex truncate">
        <AddressCopyLink
          class="truncate text-lg leading-tight"
          tooltip="click to view address"
          :address="row.fromAddressInfo.address"
          :type="row.fromAddressInfo.addressType"
          :name="row.fromAddressInfo.name"
          is-detail
          hide-eoa-icon
          center-ellipsis
        />
      </div>

      <FontAwesomeIcon
        icon="arrow-right"
        class="mx-auto text-primary/40"
      />
      <div class="flex truncate">
        <AddressCopyLink
          class="truncate text-lg leading-tight"
          tooltip="click to view address"
          :address="row.toAddressInfo.address"
          :type="row.toAddressInfo.addressType"
          :name="row.toAddressInfo.name"
          is-detail
          hide-eoa-icon
          center-ellipsis
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.transfer-row-cont {
  @apply flex flex-col p-2 gap-y-0.5 hover:bg-gradient-to-r from-primary/5 to-point-color/20 w-full relative
  rounded-lg overflow-hidden;

  &::after {
    @apply absolute top-0 left-0 w-full h-full bg-gradient-to-r from-primary/[8%] to-point-color/20 opacity-0 z-[-1];
    content: '';
    transition: opacity .3s ease;
  }

  &:hover {
    &::after {
      @apply opacity-100;
    }
  }

  .img {
    @apply w-5 h-5 rounded-full object-cover shrink-0;
  }

  .transfer-container {
    @apply grid gap-x-2 items-center;
    grid-template-columns: 1fr 1.5rem 1fr;
  }
}
</style>
